/* A frozen connection's journey, drawn left to right in plain words. */
import React from "react";
import { assessedVerdict } from "./flowAssessment";
import { placeOf, reading } from "./flowWords";
import "./FlowEvidence.css";

const STATES = {
  ok: "Got through",
  broken: "Broke here",
  suspected: "Likely here",
  unresolved: "Somewhere here",
  unknown: "Couldn't see",
};
const MARKS = { ok: "✓", broken: "✕", suspected: "?", unresolved: "?" };
const KEY = {
  ok: "Traffic got through",
  broken: "It broke here",
  suspected: "It likely broke here",
  unresolved: "It broke somewhere in here",
  unknown: "We couldn't see this part",
};
const moved = (value) => Number.isFinite(value) && value > 0;

// What this box sent during the freeze was counted arriving at the cloud. A few
// packets are always in flight when either end reads its counters.
export const delivered = (win) => Boolean(win?.matched) && moved(win.edge_fwd?.pkts)
  && moved(win.anchor_fwd?.pkts) && win.anchor_fwd.pkts + 3 >= 0.9 * win.edge_fwd.pkts;

export function pathState(episode) {
  const ev = episode?.evidence || {};
  const { segment, confidence } = assessedVerdict(episode?.verdict);
  const { anchor } = ev;
  const state = {
    device: "unknown", lan: "unknown", edge: "unknown", uplink: "unknown",
    anchor: "unknown", internet: "unknown", far: "unknown",
  };

  // Forward traffic observed after the stall establishes device-to-edge movement.
  // A reply counted at the edge does not establish delivery to the LAN device.
  if (moved(ev.device_other_sent_pkts) || moved(ev.fwd_after?.pkts) || moved(ev.retries?.pkts_out)) {
    state.device = "ok";
    state.lan = "ok";
    state.edge = "ok";
  }
  if ([ev.fwd_before, ev.rev_before, ev.fwd_after, ev.rev_after, ev.sent_total].some((p) => moved(p?.pkts))) {
    state.edge = "ok";
  }
  if (anchor?.found) state.anchor = "ok";
  // Neither unmatched lifetime counters nor independent ICMP packets establish
  // this flow's delivery through the tunnel, onward path or remote service.
  // The same interval counted at both ends does.
  const win = ev.anchor_window;
  if (delivered(win)) {
    state.uplink = "ok";
    state.anchor = "ok";
  }
  // Replies counted at the cloud during the freeze came from the service.
  if (win?.matched && moved(win.anchor_rev?.pkts)) {
    state.anchor = "ok";
    state.internet = "ok";
    state.far = "ok";
  }

  if (segment === "past_anchor") {
    state.internet = "unresolved";
    state.far = "unresolved";
  } else if (segment === "lan_or_device") {
    // The verdict deliberately does not separate the device from its LAN link.
    state.device = "unresolved";
    state.lan = "unresolved";
  } else if (placeOf(segment).at) {
    state[placeOf(segment).at] = confidence === "confirmed" ? "broken" : "suspected";
  }
  return state;
}

const NODES = [
  { key: "device", label: "The device" },
  { key: "edge", label: "This box" },
  { key: "anchor", label: "Weavian cloud" },
  { key: "far", label: "The service" },
];
const HOPS = [
  { key: "lan", label: "Office network" },
  { key: "uplink", label: "Internet lines" },
  { key: "internet", label: "Public internet" },
];

export default function FlowPath({ episode }) {
  const states = pathState(episode);
  const ev = episode?.evidence || {};
  const { where } = reading({ ...assessedVerdict(episode?.verdict), recovered: ev.recovered, closing_state: ev.closing_state });
  const names = {
    device: episode?.hostname || episode?.key?.device,
    edge: episode?.node,
    anchor: ev.anchor?.anchor,
    far: episode?.key?.dst,
  };
  const spoken = NODES.flatMap((node, i) => [
    `${node.label}: ${STATES[states[node.key]]}.`,
    HOPS[i] ? `${HOPS[i].label}: ${STATES[states[HOPS[i].key]]}.` : "",
  ]).join(" ");
  const present = new Set(Object.values(states));
  const tag = (state) => <em>{state === "unknown" ? "" : STATES[state]}</em>;

  return (
    <div className="fp-wrap">
      <p className="fp-how">Read it left to right: the connection leaves the device, passes through this box and the Weavian cloud, and reaches the service.</p>
      <div className="fp" role="img" aria-label={`${where}. ${spoken} Got through means traffic was seen moving there during the freeze, not that the part is healthy.`}>
        {NODES.map((node, i) => (
          <React.Fragment key={node.key}>
            <div className={`fp-node is-${states[node.key]}`}>
              {MARKS[states[node.key]] && <span className="fp-mark">{MARKS[states[node.key]]}</span>}
              <strong>{node.label}</strong>
              {names[node.key] && <small>{names[node.key]}</small>}
              {tag(states[node.key])}
            </div>
            {HOPS[i] && (
              <div className={`fp-hop is-${states[HOPS[i].key]}`}>
                <small>{HOPS[i].label}</small>
                <i>{MARKS[states[HOPS[i].key]] && states[HOPS[i].key] !== "ok" && <b>{MARKS[states[HOPS[i].key]]}</b>}</i>
                {tag(states[HOPS[i].key])}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="fp-key">
        {Object.keys(KEY).filter((state) => present.has(state)).map((state) => (
          <span key={state}><i className={`fp-chip ${state}`} />{KEY[state]}</span>
        ))}
      </div>
    </div>
  );
}

export function PathStrip({ segment, confidence }) {
  const { at } = placeOf(segment);
  const status = segment === "past_anchor" || segment === "lan_or_device"
    ? "unresolved" : confidence === "confirmed" ? "established" : "suspected";
  return (
    <span className="fp-strip" aria-hidden="true">
      {["device", "lan", "uplink", "internet"].map((cell) => (
        <i key={cell} className={`fp-strip-cell${at === cell || (at === "far" && cell === "internet")
          || (segment === "lan_or_device" && cell === "device") ? ` hit is-${status}` : ""}`} />
      ))}
    </span>
  );
}
